import { useState, type ReactNode } from 'react';
import { Icon } from '../../design-system/icons';
import { Dialog, DialogActions } from '../../design-system/Overlay';
import './selection-toolbar.css';

/**
 * Barre de sélection (ADR-149 §3) : elle prend la place des outils de la liste dès qu'un élément est
 * choisi — ✕, nombre, « Sélectionner les N », actions groupées. Une action hors bornes est grisée et
 * dit pourquoi ; une action destructive est en rouge, après les autres, et toujours confirmée. Les
 * boîtes de dialogue suivent `DialogActions`. Présentation : le serveur réautorise chaque commande.
 */

export interface ToolbarAction {
  id: string;
  label: string;
  icon?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Raison d'une action grisée (refus visible d'avance). */
  reason?: string;
  /** Confirmation avant l'action ; `null` ou absente : l'action part au clic. */
  confirm?: { title?: string; message?: ReactNode } | null;
  onRun: () => void;
}

export function SelectionToolbar({
  label,
  everything,
  actions,
  onClear,
  hint = true,
  children,
}: {
  /** « 3 sélectionnés », « 128 documents sélectionnés (tout le dossier) »… */
  label: string;
  /** Lien « Sélectionner les N » : tout l'ensemble filtré, résolu au serveur. */
  everything?: { label: string; onSelect: () => void } | undefined;
  actions: readonly ToolbarAction[];
  onClear: () => void;
  /** Rappel des gestes de sélection. */
  hint?: boolean;
  children?: ReactNode;
}) {
  const [pending, setPending] = useState<ToolbarAction | null>(null);
  const ordered = [...actions.filter((action) => action.danger !== true), ...actions.filter((action) => action.danger === true)];
  const run = (action: ToolbarAction): void => {
    if (action.disabled === true) return;
    if (action.confirm !== undefined && action.confirm !== null) setPending(action);
    else action.onRun();
  };
  return (
    <div className="ay-selbar" role="region" aria-label="Sélection">
      <div className="ay-selbar__bar" role="toolbar" aria-label="Actions de la sélection">
        <button type="button" className="ay-selbar__clear" aria-label="Tout désélectionner" title="Tout désélectionner (Échap)" onClick={onClear}>
          <Icon name="close" size={16} />
        </button>
        <strong className="ay-selbar__count">{label}</strong>
        {everything !== undefined && (
          <button type="button" className="ay-selbar__all" onClick={everything.onSelect}>
            {everything.label}
          </button>
        )}
        <span className="ay-selbar__actions">
          {ordered.map((action) => (
            <button
              key={action.id}
              type="button"
              className={`ay-selbar__action${action.danger === true ? ' ay-selbar__action--danger' : ''}`}
              aria-disabled={action.disabled === true ? true : undefined}
              title={action.disabled === true ? action.reason : undefined}
              onClick={() => run(action)}
            >
              {action.icon !== undefined && <Icon name={action.icon} size={15} />}
              <span>{action.label}</span>
              {action.disabled === true && action.reason !== undefined && <span className="ay-visually-hidden"> — {action.reason}</span>}
            </button>
          ))}
        </span>
        {children}
        {hint && <span className="ay-selbar__hint">Maj+clic : une plage · Ctrl+clic : ajouter ou retirer · Ctrl+A : tout</span>}
      </div>
      {pending !== null && (
        <Dialog
          title={pending.confirm?.title ?? `${pending.label} ?`}
          open
          size="sm"
          onClose={() => setPending(null)}
          footer={
            <DialogActions
              cancel={{ label: 'Annuler', onClick: () => setPending(null) }}
              {...(pending.danger === true
                ? { destructive: { label: pending.label, onClick: () => { const action = pending; setPending(null); action.onRun(); } } }
                : { primary: { label: pending.label, onClick: () => { const action = pending; setPending(null); action.onRun(); } } })}
            />
          }
        >
          <p className="ay-selbar__confirm">{pending.confirm?.message ?? `« ${pending.label} » s’appliquera à la sélection : ${label}.`}</p>
        </Dialog>
      )}
    </div>
  );
}
